/**
 * useShipping Hook - Centralized shipping calculation logic
 * Removes duplication across cart, checkout, and product pages
 */

import { useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';

// Shipping constants
const SHIPPING_CONFIG = {
  TR: {
    domestic: {
      base: 185,
      freeThreshold: 1500,
      currency: 'TRY'
    },
    express: {
      base: 350,
      freeThreshold: null,
      currency: 'TRY'
    }
  },
  INTERNATIONAL: {
    standard: {
      base: 250,
      freeThreshold: 3000,
      currency: 'TRY'
    },
    express: {
      base: 500,
      freeThreshold: null,
      currency: 'TRY'
    }
  }
};

// Weight-based pricing (per kg after first 1kg)
const WEIGHT_PRICING = {
  domestic: 15, // TRY per kg
  international: 35 // TRY per kg
};

/**
 * Calculate shipping cost based on destination and cart value
 */
export function calculateShippingCost(options) {
  const {
    destinationCountry = 'TR',
    cartSubtotal = 0,
    totalWeight = 1, // kg
    shippingType = 'standard',
    productShippingCost = null // Override from product
  } = options;

  // If product has fixed shipping cost, use it
  if (productShippingCost !== null && productShippingCost !== undefined) {
    return {
      cost: productShippingCost,
      isFree: productShippingCost === 0,
      type: 'fixed',
      currency: 'TRY'
    };
  }

  const isDomestic = destinationCountry === 'TR' || destinationCountry === 'Türkiye';
  const config = isDomestic ? SHIPPING_CONFIG.TR : SHIPPING_CONFIG.INTERNATIONAL;
  const rateConfig = shippingType === 'express' ? config.express : (isDomestic ? config.domestic : config.standard);

  // Check for free shipping
  if (rateConfig.freeThreshold && cartSubtotal >= rateConfig.freeThreshold) {
    return {
      cost: 0,
      isFree: true,
      freeThreshold: rateConfig.freeThreshold,
      type: shippingType,
      currency: rateConfig.currency
    };
  }

  // Calculate base cost
  let cost = rateConfig.base;

  // Add weight-based cost for items over 1kg
  if (totalWeight > 1) {
    const extraWeight = totalWeight - 1;
    const weightRate = isDomestic ? WEIGHT_PRICING.domestic : WEIGHT_PRICING.international;
    cost += extraWeight * weightRate;
  }

  return {
    cost: Math.round(cost * 100) / 100,
    isFree: false,
    freeThreshold: rateConfig.freeThreshold,
    amountToFreeShipping: rateConfig.freeThreshold ? Math.max(0, rateConfig.freeThreshold - cartSubtotal) : null,
    type: shippingType,
    currency: rateConfig.currency
  };
}

/**
 * Calculate estimated delivery days
 */
export function getEstimatedDelivery(destinationCountry = 'TR', shippingType = 'standard') {
  const isDomestic = destinationCountry === 'TR' || destinationCountry === 'Türkiye';

  if (isDomestic) {
    return shippingType === 'express'
      ? { min: 1, max: 2, text: '1-2 iş günü' }
      : { min: 3, max: 5, text: '3-5 iş günü' };
  }

  return shippingType === 'express'
    ? { min: 3, max: 5, text: '3-5 iş günü' }
    : { min: 7, max: 14, text: '7-14 iş günü' };
}

/**
 * useShipping Hook
 */
export function useShipping(options = {}) {
  const {
    defaultCountry = 'TR',
    defaultType = 'standard'
  } = options;

  const [destinationCountry, setDestinationCountry] = useState(defaultCountry);
  const [shippingType, setShippingType] = useState(defaultType);
  const [loading, setLoading] = useState(false);
  const [pttRates, setPttRates] = useState(null);

  /**
   * Calculate shipping for cart items
   */
  const calculateForCart = useCallback((cartItems, subtotal) => {
    // Calculate total weight from cart items
    const totalWeight = cartItems.reduce((sum, item) => {
      const productWeight = item.product?.weight || item.weight || 0.5; // Default 0.5kg
      return sum + (productWeight * (item.quantity || 1));
    }, 0);

    // Check for any product-specific shipping costs
    const hasFixedShipping = cartItems.some(item => 
      item.product?.shipping_cost !== undefined && item.product?.shipping_cost !== null
    );

    if (hasFixedShipping) {
      // Sum fixed shipping costs from products
      const fixedCost = cartItems.reduce((sum, item) => {
        if (item.product?.shipping_cost) {
          return sum + (item.product.shipping_cost * (item.quantity || 1));
        }
        return sum;
      }, 0);

      return {
        cost: fixedCost,
        isFree: fixedCost === 0,
        type: 'fixed',
        currency: 'TRY',
        totalWeight
      };
    }

    return calculateShippingCost({
      destinationCountry,
      cartSubtotal: subtotal,
      totalWeight,
      shippingType
    });
  }, [destinationCountry, shippingType]);

  /**
   * Fetch PTT Kargo rates from API
   */
  const fetchPTTRates = useCallback(async (weight, city) => {
    setLoading(true);
    try {
      const response = await api.get('/shipping/ptt/calculate-cost', {
        params: {
          weight,
          destination_city: city,
          service_type: shippingType === 'express' ? 'HIZLI_KARGO' : 'KARGO'
        }
      });
      setPttRates(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch PTT rates:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [shippingType]);

  /**
   * Get shipping options for checkout
   */
  const getShippingOptions = useMemo(() => {
    const isDomestic = destinationCountry === 'TR' || destinationCountry === 'Türkiye';

    if (isDomestic) {
      return [
        {
          id: 'standard',
          name: 'Standart Kargo',
          price: SHIPPING_CONFIG.TR.domestic.base,
          estimatedDelivery: '3-5 iş günü',
          carrier: 'PTT Kargo'
        },
        {
          id: 'express',
          name: 'Hızlı Kargo',
          price: SHIPPING_CONFIG.TR.express.base,
          estimatedDelivery: '1-2 iş günü',
          carrier: 'PTT Kargo'
        }
      ];
    }

    return [
      {
        id: 'international_standard',
        name: 'Uluslararası Standart',
        price: SHIPPING_CONFIG.INTERNATIONAL.standard.base,
        estimatedDelivery: '7-14 iş günü',
        carrier: 'PTT Kargo / Partner'
      },
      {
        id: 'international_express',
        name: 'Uluslararası Ekspres',
        price: SHIPPING_CONFIG.INTERNATIONAL.express.base,
        estimatedDelivery: '3-5 iş günü',
        carrier: 'DHL / FedEx'
      }
    ];
  }, [destinationCountry]);

  /**
   * Get free shipping progress
   */
  const getFreeShippingProgress = useCallback((currentSubtotal) => {
    const isDomestic = destinationCountry === 'TR' || destinationCountry === 'Türkiye';
    const threshold = isDomestic 
      ? SHIPPING_CONFIG.TR.domestic.freeThreshold 
      : SHIPPING_CONFIG.INTERNATIONAL.standard.freeThreshold;

    if (!threshold) {
      return { eligible: false, progress: 0, remaining: null };
    }

    const progress = Math.min((currentSubtotal / threshold) * 100, 100);
    const remaining = Math.max(0, threshold - currentSubtotal);

    return {
      eligible: currentSubtotal >= threshold,
      progress,
      remaining,
      threshold
    };
  }, [destinationCountry]);

  return {
    // State
    destinationCountry,
    shippingType,
    loading,
    pttRates,
    
    // Setters
    setDestinationCountry,
    setShippingType,
    
    // Calculators
    calculateForCart,
    calculateShippingCost: (opts) => calculateShippingCost({ ...opts, destinationCountry, shippingType }),
    getEstimatedDelivery: (country, type) => getEstimatedDelivery(country || destinationCountry, type || shippingType),
    getFreeShippingProgress,
    
    // API
    fetchPTTRates,
    
    // Options
    shippingOptions: getShippingOptions,
    
    // Config
    config: SHIPPING_CONFIG
  };
}

export default useShipping;
